import { lazy, Suspense } from 'react';
import { Routes, Route, Link } from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PageLoader } from './components/PageLoader';
import { ErrorFallback } from './components/ErrorFallback';
import { NavLink } from './components/NavLink';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useOrgMemberships } from '@/hooks/useOrgMemberships';
import { FeedbackButton } from './components/FeedbackButton';

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BountyList = lazy(() => import('./pages/BountyList'));
const BountyDetail = lazy(() => import('./pages/BountyDetail'));
const BountyCreate = lazy(() => import('./pages/BountyCreate'));
const BountyEdit = lazy(() => import('./pages/BountyEdit'));
const OrgManagement = lazy(() => import('./pages/OrgManagement'));
const Login = lazy(() => import('./pages/Login'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const OriginStory = lazy(() => import('./pages/OriginStory'));
const Contact = lazy(() => import('./pages/Contact'));
const RequestAccess = lazy(() => import('./pages/RequestAccess'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Agents = lazy(() => import('./pages/Agents'));
const Help = lazy(() => import('./pages/Help'));

function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { data: orgMemberships = [] } = useOrgMemberships({ enabled: !!user });
  const hasOrgAdminRole = orgMemberships.some((org) => org.membershipRole === 'admin');

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-2 sm:py-4 sm:px-6">
          {/* Single flex row that wraps on mobile: nav has w-full + order-last
              below sm so it wraps to its own line, while logo + logout stay
              paired on line 1. From sm up, flex-nowrap puts everything on one
              row — logo | nav (flex-1 absorbs middle) | user+logout. */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-6 sm:flex-nowrap">
            <Link
              to="/"
              className="text-xl font-bold gradient-text shrink-0 mr-auto sm:mr-0"
              aria-label="Go to dashboard"
            >
              ARLOS
            </Link>
            <nav
              className="order-last sm:order-none w-full sm:w-auto sm:flex-1 -mx-4 px-4 sm:mx-0 sm:px-0 mt-1 sm:mt-0 flex items-center gap-1 sm:gap-2 overflow-x-auto snap-x scrollbar-hide"
              aria-label="Primary"
            >
              <NavLink to="/">Dashboard</NavLink>
              <NavLink to="/bounties">Bounties</NavLink>
              <NavLink to="/agents">Agents</NavLink>
              <NavLink to="/help">Help</NavLink>
              {(user?.role === 'admin' || hasOrgAdminRole) && (
                <NavLink to="/orgs">Org Admin</NavLink>
              )}
            </nav>
            {user && (
              <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                <span className="hidden sm:inline text-sm text-muted-foreground max-w-[200px] truncate">
                  {user.name}
                </span>
                <Button variant="outline" size="sm" onClick={logout}>
                  Log out
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main>{children}</main>
      <FeedbackButton />
    </div>
  );
}

function HomePage() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <LandingPage />;
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Dashboard />
      </Suspense>
    </Layout>
  );
}

function App() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<Login />} />
            {/* Public marketing/explainer pages — reachable by anyone, no auth.
                `/protocol` renders the same explainer as the logged-out homepage;
                it gives the explainer a stable URL that works even for signed-in
                users (who get the Dashboard at `/`), so the header nav and footer
                links can point at it from anywhere. */}
            <Route path="/protocol" element={<LandingPage />} />
            <Route path="/origin-story" element={<OriginStory />} />
            <Route path="/contact" element={<Contact />} />
            {/* The front door for people without an account. Must stay public
                and above the `*` ProtectedRoute: the visitors who need it are
                by definition unable to authenticate. */}
            <Route path="/request-access" element={<RequestAccess />} />
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/bounties" element={<BountyList />} />
                        <Route path="/agents" element={<Agents />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/bounties/new" element={<BountyCreate />} />
                        <Route path="/bounties/:id/edit" element={<BountyEdit />} />
                        <Route path="/bounties/:id" element={<BountyDetail />} />
                        <Route path="/orgs" element={<OrgManagement />} />
                        <Route path="/u/:userId" element={<UserProfile />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default App;
